import React from 'react';
import { Button } from '@/components/ui/button';
import { Edit, Trash2, KeyRound, UserCheck, UserX } from 'lucide-react';
import { UserWithMembership } from '@/hooks/useUsers';
import { useIsMobile } from '@/hooks/use-mobile';
import { useAuth } from '@/contexts/AuthContext';

interface UserActionsProps {
  user: UserWithMembership;
  onEdit: (user: UserWithMembership) => void;
  onDelete: (user: UserWithMembership) => void;
  onResetPassword: (user: UserWithMembership) => void;
  onToggleStatus: (user: UserWithMembership) => void;
  canDelete: boolean;
  isTogglingStatus?: boolean;
}

const UserActions: React.FC<UserActionsProps> = ({
  user,
  onEdit,
  onDelete,
  onResetPassword,
  onToggleStatus,
  canDelete,
  isTogglingStatus = false,
}) => {
  const isMobile = useIsMobile();
  const { profile } = useAuth();

  const isOwnProfile = user.user_id === profile.user_id;

  return (
    <>
      <Button variant="outline" size="sm" onClick={() => onEdit(user)}>
        <Edit className="h-4 w-4" />
        {!isMobile && <span className="ml-2 sr-only">Edit</span>}
      </Button>
      <Button
        variant="outline"
        size="sm"
        onClick={() => onResetPassword(user)}
        className="text-blue-600 hover:text-blue-700"
      >
        <KeyRound className="h-4 w-4" />
        {!isMobile && <span className="ml-2 sr-only">Reset Password</span>}
      </Button>
      {canDelete && !isOwnProfile && (
        <Button
          variant="outline"
          size="sm"
          onClick={() => onToggleStatus(user)}
          disabled={isTogglingStatus}
          className={
            user.membership_is_active
              ? 'text-orange-600 hover:text-orange-700'
              : 'text-green-600 hover:text-green-700'
          }
        >
          {user.membership_is_active ? (
            <UserX className="h-4 w-4" />
          ) : (
            <UserCheck className="h-4 w-4" />
          )}
          {!isMobile && (
            <span className="ml-2 sr-only">
              {user.membership_is_active ? 'Disable' : 'Enable'}
            </span>
          )}
        </Button>
      )}
      {canDelete &&
        !isOwnProfile && ( // Prevent deleting self
          <Button
            variant="destructive"
            size="sm"
            onClick={() => onDelete(user)}
          >
            <Trash2 className="h-4 w-4" />
            {!isMobile && <span className="ml-2 sr-only">Delete</span>}
          </Button>
        )}
    </>
  );
};

export default UserActions;
